import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { filter, first, tap } from 'rxjs/operators';

export class FormHelpers {
	/**
	 * Validates all controls in a form
	 */
	public static validateForm(form: FormGroup, transferTargetObject = null, emitEvent: boolean = true): boolean {
		// Helper function
		const validateControl: (control: AbstractControl) => void = (control: AbstractControl) => {
			// Loop through the controls if it's an array or group
			if (control instanceof FormGroup || control instanceof FormArray) {
				for (const controlName of Object.keys(control.controls)) {
					validateControl(control.controls[controlName]);
				}
			} else if (control instanceof FormControl) {
				// Check single formcontrol
				control.markAsDirty({ onlySelf: false });
				control.updateValueAndValidity({ emitEvent: emitEvent });
			}
		};

		// Loop through all controls in the form
		for (const controlName of Object.keys(form.controls)) {
			validateControl(form.controls[controlName]);
		}

		if (form.valid && transferTargetObject) {
			this.transferFormData(form, transferTargetObject);
		}

		return form.valid;
	}

	/**
	 * Validates all controls in a form and waits for non-pending status if async.
	 */
	public static validateFormAsync(form: FormGroup, transferTargetObject = null, emitFormEvent: boolean = true): Observable<boolean> {
		return new Observable<boolean>(subscriber => {
			this.validateForm(form, null, emitFormEvent);

			let innerSubscription: Subscription;
			if (form.status === 'PENDING') {
				innerSubscription = form.statusChanges
					.pipe(
						filter(status => status !== 'PENDING'),
						first()
					)
					.subscribe(
						status => subscriber.next(status === 'VALID'),
						error => subscriber.error(error),
						() => subscriber.complete()
					);
			} else {
				subscriber.next(form.valid);
				subscriber.complete();
			}

			return () => {
				innerSubscription?.unsubscribe();
			};
		}).pipe(
			tap(valid => {
				if (valid && transferTargetObject) {
					this.transferFormData(form, transferTargetObject);
				}
			})
		);
	}

	/**
	 * transfers all control values to the target object
	 * make sure the property names are matched in definition of form and object type (case insensitive)
	 * @param form
	 * @param targetObject
	 */
	public static transferFormData(form: FormGroup, targetObject) {
		for (const controlName of Object.keys(form.controls)) {
			const targetKeys = Object.keys(targetObject);
			const matchingName =
				targetKeys.find(k => controlName === k) ||
				targetKeys.find(k => controlName.toLowerCase() === k.toLowerCase()) ||
				controlName;

			targetObject[matchingName] = form.controls[controlName].value;
		}
	}

	/**
	 * transfers all control values from the source object
	 * make sure the property names are matched in definition of form and object type (case insensitive)
	 * @param form
	 * @param sourceObject
	 */
	public static transferDataToForm(form: FormGroup, sourceObject) {
		for (const controlName of Object.keys(form.controls)) {
			const sourceKeys = Object.keys(sourceObject);
			const matchingName =
				sourceKeys.find(k => controlName === k) || sourceKeys.find(k => controlName.toLowerCase() === k.toLowerCase());

			form.controls[controlName].setValue(sourceObject[matchingName]);
		}
	}

	public static clearErrors(form: FormGroup) {
		for (const controlName of Object.keys(form.controls)) {
			form.controls[controlName].setErrors(null);
		}
	}
}
